import React, { InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import s from './DurationSlider.scss';

export type DurationValue = 2 | 4 | 6 | 8;

interface Props
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'min' | 'max' | 'step' | 'value' | 'onChange'
  > {
  /** Current selected duration in days */
  value: DurationValue;
  /** Callback when duration changes */
  onChange: (duration: DurationValue) => void;
  /** Additional CSS class name */
  className?: string;
  /** Whether the slider is disabled */
  disabled?: boolean;
}

const DURATIONS: DurationValue[] = [2, 4, 6, 8];

const DurationSlider: React.FC<Props> = ({
  value,
  onChange,
  className,
  disabled = false,
  ...rest
}) => {
  const { t } = useTranslation();

  // Handle change events (for when dragging ends)
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const duration = parseInt(event.target.value, 10) as DurationValue;

    // Only call onChange if the value is actually different from current value
    if (duration !== value) {
      onChange(duration);
    }
  };

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const duration = parseInt(target.value, 10) as DurationValue;

    if (duration !== value) {
      onChange(duration);
    }
  };

  return (
    <div className={c(s['duration-slider'], className)}>
      <div className={s['slider-container']}>
        <input
          {...rest}
          type="range"
          min={2}
          max={8}
          step={2}
          value={value}
          onChange={handleChange}
          onInput={handleInput}
          disabled={disabled}
          className={s['slider']}
          aria-label={t('boostDeviation.modal.chooseDurationAriaLabel')}
        />
        <div className={s['labels']}>
          {DURATIONS.map((duration, index) => (
            <button
              key={duration}
              type="button"
              className={c(s['label'], {
                [s['label--active']]: duration === value,
                [s['label--disabled']]: disabled,
              })}
              onClick={() => onChange(duration)}
              disabled={disabled}
              aria-pressed={duration === value}
              aria-label={t('boostDeviation.modal.durationDaysAriaLabel', {
                duration,
              })}
            >
              {t('boostDeviation.modal.durationDays', { duration })}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

DurationSlider.displayName = 'DurationSlider';

export default DurationSlider;
